import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Dialog, DialogContent, Box, IconButton } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import CustomButton from 'components/Fileds/CustomButton';
import CustomTypography from 'components/Fileds/CustomTypography';
import ManageSettingsContainer from './container';
import {
    useGetSettingsQuery,
    useUpdateSettingsMutation,
    useToggleSettingMutation,
    useGenerateVendorQrMutation,
    useGetVendorQrQuery,
    useCreateStripeConnectOnboardMutation,
    useGetStripeConnectStatusQuery
} from './services';
import { getFullQrUrl } from 'utils/function';
import ViewQrModal from 'components/Modal/ViewQrModal';

const ManageSettings = () => {
    const posData = useSelector((state) => state?.posData);
    const vendorData = posData?.vendor;

    const { data: settingsData, isLoading, refetch: refetchSettings } = useGetSettingsQuery();
    const [updateSettings, { isLoading: isUpdating }] = useUpdateSettingsMutation();
    const [toggleSetting, { isLoading: isToggling }] = useToggleSettingMutation();
    const [generateVendorQr, { isLoading: isGeneratingQr }] = useGenerateVendorQrMutation();
    const { data: vendorQrData, isLoading: isVendorQrLoading } = useGetVendorQrQuery();
    const [createStripeConnectOnboard, { isLoading: isOnboardingStripe }] = useCreateStripeConnectOnboardMutation();
    const { data: stripeStatusData, isLoading: isStripeStatusLoading, refetch: refetchStripeStatus } = useGetStripeConnectStatusQuery();

    const [qrModalData, setQrModalData] = useState(null);

    // Stripe Onboarding Status Modal State
    const [stripeModal, setStripeModal] = useState({
        open: false,
        type: 'success', // 'success' | 'error'
        title: '',
        message: ''
    });

    const initialValues = {
        takeAway: false,
        delivery: false,
        tableBookingFlow: false,
        collectionPoints: false,
        eMenuUserLogin: false,
        driveThrough: false,
        chefUserLogin: false,
        packingBufferTime: 15,
        tableBookingPrice: 50,
        max_guest_count: 15,
        package_charge_enabled: false,
        package_charge_price: '',
        free_delivery_up_to_miles: '',
        delivery_tiers: [],
        auto_print_qr_orders: false,
        mandatoryCustomerDetailsDineIn: true,
        printer_ip: '',
        printer_port: '',
        calendar: false,
        inventory: false,
        serve_panel: false,
    };

    const validationSchema = Yup.object({
        packingBufferTime: Yup.number().min(0).max(120).required('Required'),
        tableBookingPrice: Yup.number().min(0).required('Required'),
        max_guest_count: Yup.number().min(0).max(99, 'Only 2 digits allowed').required('Required'),
        printer_ip: Yup.string().when('auto_print_qr_orders', {
            is: true,
            then: (schema) => schema
                .matches(
                    /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)\.){3}(25[0-5]|(2[0-4]|1\d|[1-9]|)\d)$/,
                    'Must be a valid IPv4 address (e.g. 192.168.1.200)'
                )
                .required('Printer IP is required when Auto-Print is enabled'),
            otherwise: (schema) => schema.nullable().notRequired(),
        }),
        printer_port: Yup.number()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .typeError('Port must be a valid number')
            .min(1, 'Port must be between 1 and 65535')
            .max(65535, 'Port must be between 1 and 65535')
            .nullable()
            .notRequired(),
        package_charge_price: Yup.number().when('package_charge_enabled', {
            is: true,
            then: (schema) => schema
                .typeError('Package charge must be a number')
                .min(0.01, 'Package charge must be greater than 0')
                .required('Package charge price is required when package charge is enabled'),
            otherwise: (schema) => schema.nullable().notRequired(),
        }),
        free_delivery_up_to_miles: Yup.number()
            .typeError('Must be a valid number')
            .min(0, 'Cannot be negative')
            .nullable()
            .notRequired(),
        delivery_tiers: Yup.array().of(
            Yup.object({
                up_to_miles: Yup.number()
                    .typeError('Must be a number')
                    .required('Up to (miles) is required')
                    .min(0, 'Cannot be negative')
                    .test(
                        'greater-than-from',
                        'Must be greater than From (mi)',
                        function (value) {
                            const fromMiles = parseFloat(this.parent?.from_miles);
                            const upTo = parseFloat(value);
                            if (isNaN(upTo) || isNaN(fromMiles)) return true;
                            return upTo > fromMiles;
                        }
                    ),
                fee: Yup.number()
                    .typeError('Must be a number')
                    .required('Fee is required')
                    .min(0, 'Cannot be negative'),
            })
        ),
    });

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values) => {
            const body = {
                take_away: values.takeAway ? 1 : 0,
                delivery: values.delivery ? 1 : 0,
                table_booking: values.tableBookingFlow ? 1 : 0,
                collection_point: values.collectionPoints ? 1 : 0,
                drive_thru: values.driveThrough ? 1 : 0,
                chef_login: values.chefUserLogin ? 1 : 0,
                emenu_login: values.eMenuUserLogin ? 1 : 0,
                packing_buffer_time: values.packingBufferTime,
                table_booking_price: values.tableBookingPrice,
                max_guest_count: values.max_guest_count,
                package_charge_enabled: values.package_charge_enabled ? 1 : 0,
                package_charge_price: values.package_charge_enabled ? parseFloat(values.package_charge_price) || 0 : 0,
                free_delivery_up_to_miles: values.free_delivery_up_to_miles !== '' && values.free_delivery_up_to_miles != null ? parseFloat(values.free_delivery_up_to_miles) : null,
                delivery_tiers: values.delivery_tiers.map((tier) => ({
                    from_miles: parseFloat(tier.from_miles),
                    up_to_miles: parseFloat(tier.up_to_miles),
                    fee: parseFloat(tier.fee),
                })),
                auto_print_qr_orders: values.auto_print_qr_orders ? 1 : 0,
                mandatory_customer_details_dine_in: values.mandatoryCustomerDetailsDineIn ? 1 : 0,
                printer_ip: values.printer_ip ? values.printer_ip.trim() : null,
                printer_port: values.printer_port !== '' && values.printer_port != null ? parseInt(values.printer_port, 10) : null,
                calendar: values.calendar ? 1 : 0,
                inventory: values.inventory ? 1 : 0,
                serve_panel: values.serve_panel ? 1 : 0,
            };

            try {
                const response = await updateSettings(body).unwrap();
                console.log('Settings updated successfully', response);
            } catch (error) {
                console.error('Failed to update settings:', error);
            }
        },
    });

    useEffect(() => {
        if (settingsData?.data?.settings) {
            const settings = settingsData.data.settings;
            const tiers = settingsData.data.delivery_tiers || [];
            formik.setValues({
                takeAway: settings.take_away === 1,
                delivery: settings.delivery === 1,
                tableBookingFlow: settings.table_booking === 1,
                collectionPoints: settings.collection_point === 1,
                driveThrough: settings.drive_thru === 1,
                chefUserLogin: settings.chef_login === 1,
                eMenuUserLogin: settings.emenu_login === 1,
                packingBufferTime: settings.packing_buffer_time || 0,
                tableBookingPrice: settings.table_booking_price || 0,
                max_guest_count: settings.max_guest_count || 0,
                package_charge_enabled: settings.package_charge_enabled === 1,
                package_charge_price: settings.package_charge_price != null ? settings.package_charge_price : '',
                free_delivery_up_to_miles: settings.free_delivery_up_to_miles != null ? settings.free_delivery_up_to_miles : '',
                delivery_tiers: tiers.map((t) => ({
                    from_miles: t.from_miles,
                    up_to_miles: t.up_to_miles,
                    fee: t.fee,
                })),
                auto_print_qr_orders: settings.auto_print_qr_orders === 1,
                mandatoryCustomerDetailsDineIn: settings.mandatory_customer_details_dine_in !== undefined ? (settings.mandatory_customer_details_dine_in === 1 || settings.mandatory_customer_details_dine_in === true || settings.mandatory_customer_details_dine_in === "1") : true,
                printer_ip: settings.printer_ip || '',
                printer_port: settings.printer_port != null ? settings.printer_port : '',
                calendar: settings.calendar === 1 || settings.calendar === true || settings.calendar === "1",
                inventory: settings.inventory === 1 || settings.inventory === true || settings.inventory === "1",
                serve_panel: settings.serve_panel === 1 || settings.serve_panel === true || settings.serve_panel === "1",
            });
        }
    }, [settingsData]);

    const handleGenerateQr = async () => {
        try {
            await generateVendorQr().unwrap();
        } catch (error) {
            console.error("Failed to generate vendor QR code:", error);
        }
    };

    const orderLink = vendorData?.order_link || posData?.order_link || '';
    const orderQrCode = vendorData?.order_qr_code || posData?.order_qr_code || '';

    // Extract QR code URL from response (checking common data wrapper properties)
    const qrCodeUrl = vendorQrData?.data?.qr_code_url || vendorQrData?.data?.qr_code || vendorQrData?.qr_code_url || vendorQrData?.qr_code;

    const handleViewTimeOutQr = () => {
        setQrModalData({
            open: true,
            table_no: 'Vendor',
            isVendor: true,
            qr_code_url: qrCodeUrl,
        });
    };

    const handleViewOrderQr = () => {
        const fullUrl = getFullQrUrl(orderQrCode);
        setQrModalData({
            open: true,
            table_no: vendorData?.name || 'Customer Ordering',
            isVendor: false,
            qr_code_url: fullUrl,
            title: 'Customer Order QR Code',
            subtitle: 'Scan to view menu & place customer orders',
            displayLabel: vendorData?.name ? `${vendorData.name} - Order QR` : 'Customer Order QR',
            fileName: `${(vendorData?.name || 'Customer_Order').replace(/[^a-zA-Z0-9_-]/g, '_')}_QR.png`,
        });
    };

    const [selectedCountry, setSelectedCountry] = useState('GB');

    const handleStripeOnboard = async (params = {}) => {
        try {
            const body = {
                country: params.country || selectedCountry,
                reset: params.reset || false,
            };
            const response = await createStripeConnectOnboard(body).unwrap();
            const redirectUrl = response?.data?.onboarding_url || response?.data?.url || response?.onboarding_url || response?.url;
            if (redirectUrl) {
                window.location.href = redirectUrl;
            } else {
                console.error("No redirect URL returned in Stripe response:", response);
            }
        } catch (error) {
            console.error("Failed to generate Stripe onboarding link:", error);
        }
    };

    // Extract Stripe Connect info from settings response or status endpoint
    const stripeAccountId = stripeStatusData?.data?.stripe_account_id || settingsData?.data?.stripe_account_id;
    const stripeOnboardingCompleted = stripeStatusData?.data?.stripe_onboarding_completed !== undefined
        ? stripeStatusData.data.stripe_onboarding_completed
        : settingsData?.data?.stripe_onboarding_completed;

    // Single-trigger effect on component mount when returning from Stripe onboarding
    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const stripeStatus = queryParams.get('stripe_status') || queryParams.get('stripe');

        if (stripeStatus) {
            // Immediately remove query parameter from browser address bar so re-renders or page refresh will NOT re-trigger the modal
            window.history.replaceState(null, '', window.location.pathname);

            // Trigger background refetch of latest status and settings
            refetchStripeStatus();
            refetchSettings();

            if (stripeStatus === 'success' || stripeStatus === 'true') {
                setStripeModal({
                    open: true,
                    type: 'success',
                    title: 'Stripe Connected Successfully!',
                    message: 'Your Stripe Connect account has been successfully set up. You can now accept online card payments for your restaurant.'
                });
            } else if (stripeStatus === 'refresh' || stripeStatus === 'failed' || stripeStatus === 'false' || stripeStatus === 'error') {
                setStripeModal({
                    open: true,
                    type: 'error',
                    title: 'Stripe Onboarding Incomplete',
                    message: 'Stripe onboarding was not completed. Please click "Connect Stripe Account" to try again.'
                });
            }
        }
    }, []); // Empty dependency array ensures this effect runs ONCE on component mount

    const formikKeyToDbKey = {
        takeAway: 'take_away',
        delivery: 'delivery',
        tableBookingFlow: 'table_booking',
        collectionPoints: 'collection_point',
        eMenuUserLogin: 'emenu_login',
        driveThrough: 'drive_thru',
        chefUserLogin: 'chef_login',
        mandatoryCustomerDetailsDineIn: 'mandatory_customer_details_dine_in',
        calendar: 'calendar',
        inventory: 'inventory',
        serve_panel: 'serve_panel',
    };

    const handleToggleSetting = async (formikKey, checked) => {
        const dbKey = formikKeyToDbKey[formikKey] || formikKey;
        formik.setFieldValue(formikKey, checked);
        try {
            await toggleSetting({ [dbKey]: checked ? 1 : 0 }).unwrap();
        } catch (error) {
            console.error(`Failed to toggle setting ${dbKey}:`, error);
            formik.setFieldValue(formikKey, !checked);
        }
    };

    return (
        <>
            <ManageSettingsContainer
                formik={formik}
                permissions={settingsData?.data?.permissions}
                loading={isLoading}
                isUpdating={isUpdating}
                onToggleSetting={handleToggleSetting}
                qrCodeUrl={qrCodeUrl}
                isGeneratingQr={isGeneratingQr}
                isVendorQrLoading={isVendorQrLoading}
                onGenerateQr={handleGenerateQr}
                onViewQr={handleViewTimeOutQr}
                orderLink={orderLink}
                orderQrCode={orderQrCode}
                onViewOrderQr={handleViewOrderQr}
                stripeAccountId={stripeAccountId}
                stripeOnboardingCompleted={stripeOnboardingCompleted}
                isOnboardingStripe={isOnboardingStripe}
                isStripeStatusLoading={isStripeStatusLoading}
                onStripeOnboard={handleStripeOnboard}
                refetchStripeStatus={refetchStripeStatus}
                selectedCountry={selectedCountry}
                setSelectedCountry={setSelectedCountry}
            />

            <ViewQrModal
                open={Boolean(qrModalData?.open)}
                onClose={() => setQrModalData(null)}
                table={qrModalData}
            />

            {/* Stripe Onboarding Result Modal */}
            <Dialog
                open={stripeModal.open}
                onClose={() => setStripeModal((prev) => ({ ...prev, open: false }))}
                maxWidth="xs"
                fullWidth
                className="generic-dialog-container"
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        padding: '16px 20px',
                        textAlign: 'center'
                    }
                }}
            >
                <Box display="flex" justifyContent="flex-end" sx={{ m: 0, p: 0 }}>
                    <IconButton onClick={() => setStripeModal((prev) => ({ ...prev, open: false }))} className="close-icon-container">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <DialogContent style={{ paddingTop: 0, paddingBottom: '20px' }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                        {stripeModal.type === 'success' ? (
                            <CheckCircleOutlineIcon style={{ fontSize: 60, color: '#10B981' }} />
                        ) : (
                            <ErrorOutlineIcon style={{ fontSize: 60, color: '#EF4444' }} />
                        )}

                        <CustomTypography
                            variant="h5"
                            label={stripeModal.title}
                            sx={{ fontSize: '20px', fontWeight: 700, mb: 0 }}
                        />

                        <CustomTypography
                            variant="body2"
                            label={stripeModal.message}
                            sx={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.5, textAlign: 'center' }}
                        />

                        <CustomButton
                            variant="contained"
                            onClick={() => setStripeModal((prev) => ({ ...prev, open: false }))}
                            sx={{
                                marginTop: '12px',
                                backgroundColor: stripeModal.type === 'success' ? '#10B981' : '#EF4444',
                                color: '#FFF',
                                borderRadius: '10px',
                                padding: '8px 32px',
                                height: '45px',
                                textTransform: 'none',
                                fontWeight: 700,
                                '&:hover': {
                                    backgroundColor: stripeModal.type === 'success' ? '#059669' : '#DC2626',
                                }
                            }}
                        >
                            Got It
                        </CustomButton>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ManageSettings;

